/**
 * Fresh-canvas landing screen: a subtle menu-bar header (logo + a static greeting on the
 * left, the date/time on the right) above a free-floating composer card and four prefill
 * idea cards. Shown only on an untouched canvas (no message ever sent, no nodes); the first
 * send creates the canvas session and the panel swaps the landing for the chat surface.
 *
 * Self-contained: it reads the active canvas + send path from CanvasFloatingChatContext.
 */

import LogoIcon from "@/svg/brand/cohesive-logo-icon.svg?react";
import { cn } from "@/utils";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CANVAS_COMPOSER_SUGGESTIONS,
  type CanvasComposerSuggestion,
} from "../../config/canvasComposerSuggestions";
import {
  formatCanvasGreetingDate,
  pickCanvasGreeting,
} from "../../config/canvasGreetings";
import { useCanvasFloatingChat } from "../../context/CanvasFloatingChatContext";
import { useCanvasRoute } from "../../context/CanvasRouteContext";
import {
  CanvasComposerInput,
  type CanvasComposerInputHandle,
} from "../CanvasComposerInput";

const CARD_WIDTH = 540;

/**
 * Elevation only — the inner `ComposerShell` already owns the framed surface
 * (1.5px border + rust focus ring + bg). Wrapping it in a second bordered/
 * backgrounded card double-framed the input (a stray ring behind the
 * composer); leave the rounding to match the shell and skip `overflow-hidden`
 * so the focus ring isn't clipped.
 */
const COMPOSER_CARD_CLASS = "rounded-xl shadow-elev-2";

// ---------------------------------------------------------------------------
// Menu-bar greeting header
// ---------------------------------------------------------------------------

function GreetingBar() {
  // Chosen once per mount — static, no on-screen rotation.
  const greeting = useMemo(() => pickCanvasGreeting(), []);

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    // Keep the clock honest without animating the greeting.
    const id = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div className="mb-3 flex w-full items-center justify-between gap-4 px-1.5 select-none">
      <div className="flex min-w-0 items-center gap-2.5">
        <LogoIcon className="h-[18px] w-[18px] shrink-0" />
        <span className="truncate text-sm font-medium text-foreground/80">
          {greeting}
        </span>
      </div>
      <span className="shrink-0 text-sm tabular-nums text-muted-foreground">
        {formatCanvasGreetingDate(now)}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Landing screen
// ---------------------------------------------------------------------------

export function CanvasLanding() {
  const { canvasId } = useCanvasRoute();
  const { send, isSubmitting } = useCanvasFloatingChat();
  const reduceMotion = useReducedMotion();
  const inputHandle = useRef<CanvasComposerInputHandle>(null);

  const handleSuggestion = useCallback((s: CanvasComposerSuggestion) => {
    inputHandle.current?.setMessage(s.prompt);
  }, []);

  if (!canvasId) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="canvas-landing"
        className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center"
        initial={reduceMotion ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -12 }}
        transition={{ duration: 0.3, ease: [0.2, 0, 0, 1] }}
      >
        <div
          className="pointer-events-auto flex w-full flex-col items-stretch px-6"
          style={{ maxWidth: CARD_WIDTH }}
        >
          <GreetingBar />

          {/* Free-floating composer card */}
          <motion.div
            initial={reduceMotion ? false : { opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.35, ease: [0.2, 0, 0, 1], delay: 0.05 }}
            className={cn("w-full", COMPOSER_CARD_CLASS)}
          >
            <CanvasComposerInput
              canvasId={canvasId}
              handleRef={inputHandle}
              isSubmitting={isSubmitting}
              onSubmit={send}
              autoFocus
              placeholder="Ask anything, or describe what you want to create…"
            />
          </motion.div>

          {/* Prefill idea cards */}
          <div className="mt-4 grid w-full grid-cols-1 gap-2.5 sm:grid-cols-2">
            {CANVAS_COMPOSER_SUGGESTIONS.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.button
                  key={s.id}
                  type="button"
                  initial={reduceMotion ? false : { opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.25,
                    ease: [0.2, 0, 0, 1],
                    delay: 0.12 + i * 0.05,
                  }}
                  // Keep the editor focused so the prefilled text lands ready to send.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSuggestion(s)}
                  className={cn(
                    "group flex items-start gap-3 rounded-xl border border-border/70 bg-surface-card/60 p-3 text-left",
                    "transition-all duration-150 ease-out hover:border-border hover:bg-surface-card hover:shadow-sm",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  )}
                >
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-muted/60 text-muted-foreground transition-colors group-hover:text-foreground">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="flex min-w-0 flex-col">
                    <span className="text-sm font-medium text-foreground">
                      {s.label}
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
                      {s.hint}
                    </span>
                  </span>
                </motion.button>
              );
            })}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
